import { useState, useEffect } from 'react';
import { Input, InputGroup, InputLeftElement, Box } from '@chakra-ui/react';
import { Checkbox, Text, Colors } from '@mercator-gcp/grout';
import InfoMarker from 'components/elements/InfoMarker';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import Header from 'components/widgets/SearchFilterWizard/steps/components/Header';
import SectionWrapper from 'components/widgets/SearchFilterWizard/steps/components/SectionWrapper';
import {
  useDashboardCostState,
  useDashboardCostIncludeUnknownState,
} from 'state/dashboard';
import {
  textInputToFormattedIntegerMiddleware,
  removeCommas,
  formatNumberWithCommas,
} from 'utils/formMiddleware';

const inputContainerStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: 'padding_16',
  marginTop: 'padding_16',
  '@media (max-width: 1024px)': {
    flexDirection: 'column',
    gap: 'padding_12',
    marginTop: 'padding_12',
  },
};

const inputStyle = {
  border: '2px solid #EAECEA',
  borderRadius: '4px',
};

const labelStyle = {
  marginBottom: 'padding_8',
  fontSize: '14px',
  fontWeight: 500,
  color: Colors.ArmyGreen900,
};

const includeUnknownValueStyle = {
  columnGap: 'padding_4',
};

type ProjectValueStepProps = {
  projectValue: { min: number; max: number };
  onProjectValueChange: (value: { min: number; max: number }) => void;
};

const ProjectValueStep = ({
  projectValue,
  onProjectValueChange,
}: ProjectValueStepProps) => {
  const [, setCost] = useDashboardCostState();
  const [includeUnknown, setIncludeUnknown] = useDashboardCostIncludeUnknownState();
  const [minValue, setMinValue] = useState(
    projectValue.min > 0 ? formatNumberWithCommas(projectValue.min) : '',
  );
  const [maxValue, setMaxValue] = useState(
    projectValue.max > 0 ? formatNumberWithCommas(projectValue.max) : '',
  );

  useEffect(() => {
    setMinValue(projectValue.min > 0 ? formatNumberWithCommas(projectValue.min) : '');
    setMaxValue(projectValue.max > 0 ? formatNumberWithCommas(projectValue.max) : '');
  }, [projectValue]);

  // Set include unknown to true by default on mount
  useEffect(() => {
    if (includeUnknown === undefined || includeUnknown === null) {
      setIncludeUnknown(true);
    }
  }, [includeUnknown, setIncludeUnknown]);

  const handleMinChange = (value: string) => {
    const formatted = textInputToFormattedIntegerMiddleware({ target: { value } } as any);
    setMinValue(formatted.target.value);
    const numericValue = formatted.target.value
      ? Number(removeCommas(formatted.target.value))
      : 0;
    const newValue = { ...projectValue, min: numericValue };
    onProjectValueChange(newValue);
    setCost({ min: numericValue, max: projectValue.max });
  };

  const handleMaxChange = (value: string) => {
    const formatted = textInputToFormattedIntegerMiddleware({ target: { value } } as any);
    setMaxValue(formatted.target.value);
    const numericValue = formatted.target.value
      ? Number(removeCommas(formatted.target.value))
      : 0;
    const newValue = { ...projectValue, max: numericValue };
    onProjectValueChange(newValue);
    setCost({ min: projectValue.min, max: numericValue });
  };

  const handleIncludeUnknownChange = () => {
    setIncludeUnknown(!includeUnknown);
  };

  return (
    <SectionWrapper>
      <Header
        title="What is your target project range?"
        description="Specify your preferred value range to help us surface the highest-impact opportunities for your pipeline."
      />
      <Box sx={inputContainerStyle}>
        <FlexColumn sx={{ flex: 1 }}>
          <Text.SmallLabel sx={labelStyle}>Min Value</Text.SmallLabel>
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">$</InputLeftElement>
            <Input
              size="md"
              sx={inputStyle}
              border="1px solid"
              focusBorderColor="brand.green"
              errorBorderColor="brand.red"
              value={minValue}
              onChange={(e) => handleMinChange(e.target.value)}
              type="text"
              placeholder="750,000"
            />
          </InputGroup>
        </FlexColumn>
        <FlexColumn sx={{ flex: 1 }}>
          <Text.SmallLabel sx={labelStyle}>Max Value</Text.SmallLabel>
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">$</InputLeftElement>
            <Input
              size="md"
              sx={inputStyle}
              border="1px solid"
              focusBorderColor="brand.green"
              errorBorderColor="brand.red"
              value={maxValue}
              onChange={(e) => handleMaxChange(e.target.value)}
              type="text"
              placeholder="25,000,000"
            />
          </InputGroup>
        </FlexColumn>
      </Box>
      <FlexRow sx={{ marginTop: 'padding_16', alignItems: 'flex-start' }}>
        <Checkbox
          isChecked={includeUnknown ?? true}
          onToggle={() => handleIncludeUnknownChange()}
        >
          <FlexRow sx={includeUnknownValueStyle}>
            <Text.Body color={Colors.TextQuiet}>
              Include projects with unknown values
            </Text.Body>
            <InfoMarker label="Data availability varies by city. We recommend including unknown values to broaden search results when using multiple filters." />
          </FlexRow>
        </Checkbox>
      </FlexRow>
    </SectionWrapper>
  );
};

export default ProjectValueStep;
